/**
 * Shared onboarding defaults — v2 safe file.
 * Avoids modifying BusinessSignup.tsx (v1 frozen).
 * BusinessSignupData type is imported directly from BusinessSignup.tsx.
 */
import type { BusinessSignupData } from '@/components/onboarding/magic-steps/BusinessSignup';

export const defaultFormData: BusinessSignupData = {
  // Account Setup
  fullName: '',
  email: '',
  phone: '',
  password: '',
  ssoProvider: '',
  ssoConnected: false,

  // Business Setup
  businessName: '',
  businessAddress: '',
  businessSuite: '',
  timezone: (() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return ['America/New_York','America/Chicago','America/Denver','America/Phoenix',
              'America/Los_Angeles','America/Anchorage','Pacific/Honolulu'].includes(tz)
        ? tz : 'America/New_York';
    } catch { return 'America/New_York'; }
  })(),
  businessType: 'solo',
  bookingUrl: '',
  stripeConnected: false,

  // Branding Setup
  title: '',
  bio: '',
  logoFile: null,
  logoPreview: '',
  logoEmoji: '',

  // Schedule Setup
  startDate: new Date().toISOString().split('T')[0],
  // All days disabled by default — user must explicitly enable days on the Schedule step.
  // This prevents silently creating bookable hours without user confirmation.
  workingHours: {
    monday:    { enabled: false, startTime: '09:00', endTime: '17:00' },
    tuesday:   { enabled: false, startTime: '09:00', endTime: '17:00' },
    wednesday: { enabled: false, startTime: '09:00', endTime: '17:00' },
    thursday:  { enabled: false, startTime: '09:00', endTime: '17:00' },
    friday:    { enabled: false, startTime: '09:00', endTime: '17:00' },
    saturday:  { enabled: false, startTime: '10:00', endTime: '15:00' },
    sunday:    { enabled: false, startTime: '10:00', endTime: '15:00' },
  },
  googleCalendarConnected: false,
  googleCalendarId: '',

  // Notification Setup (platform defaults — not exposed in v2 UI)
  clientNotifications: true,
  reminderTiming: '24h',
  reminderMethod: 'email',
  adminNewBookings: true,
  adminCancellations: true,

  // Payment Setup
  stripeAccountId: '',
  stripeDashboardUrl: '',
  stripeSetupDecided: false,

  // Services Setup
  services: [],

  // Completion
  completedSteps: [],
};

export const V2_SUPPORTED_TIMEZONES = [
  { value: 'America/New_York',    label: 'Eastern Time (ET)' },
  { value: 'America/Chicago',     label: 'Central Time (CT)' },
  { value: 'America/Denver',      label: 'Mountain Time (MT)' },
  { value: 'America/Phoenix',     label: 'Arizona (MST)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage',   label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu',    label: 'Hawaii (HT)' },
];

export const V2_DAYS = [
  { key: 'monday',    label: 'Monday',    short: 'Mon' },
  { key: 'tuesday',   label: 'Tuesday',   short: 'Tue' },
  { key: 'wednesday', label: 'Wednesday', short: 'Wed' },
  { key: 'thursday',  label: 'Thursday',  short: 'Thu' },
  { key: 'friday',    label: 'Friday',    short: 'Fri' },
  { key: 'saturday',  label: 'Saturday',  short: 'Sat' },
  { key: 'sunday',    label: 'Sunday',    short: 'Sun' },
] as const;

export type V2DayKey = typeof V2_DAYS[number]['key'];

/** Derive detected timezone, falling back to Eastern if unsupported. */
export function detectSupportedTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return V2_SUPPORTED_TIMEZONES.find(t => t.value === tz)?.value ?? 'America/New_York';
  } catch {
    return 'America/New_York';
  }
}

/** Slugify a business name into a booking-URL-safe handle. */
export function v2Slugify(name: string): string {
  return (name || '')
    .toLowerCase()
    .trim()
    .replace(/['']/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 30)
    .replace(/-+$/, '');
}
